import styles from './TerminateElectronic.module.scss'
import { Button, Checkbox, Field as TextField, SelectSearch, Title } from '@/ui'
import { Field, Form, useFormikContext } from 'formik'
import { Loader } from '@/components/Loader'
import { Grid } from '@mui/material'
import { DatePicker } from '@/components/DatePicker'
import { Add, Trash } from '@app/icons'
import { HarmfulCheckboxBlock } from '@features/contracts/harmfulCheckboxBlock'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { contractsThunks, userSelectors } from '@/entity'
import { useTranslation } from 'react-i18next'
import { message } from 'antd'
import { LANGUAGE_ENUM, useElectronicTermination } from '@/shared'
import { isHarmfulWorkingConditions } from '@/utils'
import { LoadingScreen } from '@/components/Loader'
import { contractsSelectors } from '@/entity/contracts'
import cl from 'classnames'
import { getCurrentLanguage } from '@/i18next'
import { terminateStatus } from '@features/contracts/actions/terminate/ContractTerminationForm'
import { useTerminationReasonsForInputs } from '@features/contracts/hooks/useTerminationReasonsForInputs'

const AdditionalReasonSelect = ({ setAdditionalReasons, index, value, removeReason, terminationReasons }) => {
	const { t } = useTranslation()

	const handleReasonSelect = (data) => {
		setAdditionalReasons((value) => {
			const newVal = [...value]
			newVal[index] = data
			return newVal
		})
	}

	const handleReasonRemove = () => {
		removeReason(index)
	}

	return (
		<div className={styles.select__wrapper}>
			<SelectSearch value={value} onChange={handleReasonSelect} className="flex-1" options={terminationReasons} />
			<Button transparent onClick={handleReasonRemove} buttonClassName={styles.buttonRed}>
				<Trash className={styles.iconRed} />
				{t('remove')}
			</Button>
		</div>
	)
}

const getFormatDate = (value) => {
	const dateArr = value?.split('.')
	if (dateArr) {
		return `${dateArr?.[1]}.${dateArr?.[0]}.${dateArr?.[2]}`
	} else {
		return false
	}
}

export const TerminateElectronicForm = ({
	contract,
	createTerminationApp,
	isCreation,
	onUpdate,
	setOpenRevoke,
	handleOpenAdditional,
	setOpenDecline,
	setOpenSign,
	setAdditionalReasons,
	additionalReasons,
	setIsLoading,
	isState,
	onClose,
	statusKdp,
}) => {
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const dispatch = useDispatch()
	const lang = getCurrentLanguage()
	const { setFieldValue, values, isValid } = useFormikContext()

	const [stateLoading, setStateLoading] = useState({})

	const { initEmployer, termDate, termReasonsList, note, birthDate, dworkingConditionsCode, termExternalId, age } =
		contract

	const activeRole = useSelector(userSelectors.activeRole)
	const oopvSendByBin = useSelector(contractsSelectors.oopvSendByBin)
	const isEmployee = activeRole === 'ROLE_EMPLOYEE'
	const isEmployer = activeRole === 'ROLE_EMPLOYER'

	const isTeenager = Number.isFinite(age) && age < 18
	const isTeenager16 = Number.isFinite(age) && age < 16
	const { flDataLoading } = useSelector((state) => state.contracts.updateIin)
	const [annuity, setAnnuity] = useState(false)
	const [isDisabledButton, setIsDisabledButton] = useState(false)

	const { terminationIsLoading, terminationReasons, handleSign, handleSignWithoutEmployee } = useElectronicTermination(
		onUpdate,
		onClose
	)

	// Убираем ТОЛЬКО тех, у кого parentId === null и при этом есть "дети" (элементы, где parentId === id этого родителя).
	const terminationReasonsForInputs = useTerminationReasonsForInputs(terminationReasons)

	useEffect(() => {
		if (contract.dcontractStatus?.code !== '2') {
			let body = {
				data: { iin: contract.iin },
			}
			dispatch(contractsThunks.getOopvSendByBin(body))
		}
	}, [])

	useEffect(() => {
		setIsDisabledButton(isTeenager16 && statusKdp.code !== '3')
	}, [isTeenager16, statusKdp])

	useEffect(() => {
		setIsLoading(terminationIsLoading)
	}, [terminationIsLoading])

	const handler = (key, value) => {
		setStateLoading((prevState) => ({
			...prevState,
			[key]: value, // Обновляем состояние для конкретного ключа
		}))
	}

	const getFlDataRepresentative = (value) => {
		if (!value || value.length < 12) {
			return
		}
		handler('representativeData', true)
		let body = {
			data: { iin: value },
			onSuccess: (res) => {
				if (res.fullNamePerson !== null) {
					setFieldValue('representativeName', res.fullNamePerson || '')
				} else {
					message.error(t('person_not_found'), 5)
				}

				handler('representativeData', false)
			},
			onError: (error) => {
				message.error(
					'Ошибка обновления данных:' + error.response ? error.response.data.message : t('unknown_error'),
					10
				)
				handler('representativeData', false) // Завершаем загрузку в случае ошибки
			},
		}
		dispatch(contractsThunks.getFLData(body))
	}

	const employeeOrEmployer = () => {
		if (isCreation) return isEmployer
		return initEmployer
	}

	const handleAddReasonClick = (value) => {
		setAdditionalReasons((value) => [...value, null])
	}

	const handleRemoveReasonClick = (index) => {
		setAdditionalReasons((value) => {
			const newArr = [...value]
			newArr.splice(index, 1)
			return newArr
		})
	}

	const openSignWindow = (data) => {
		onSignClick(data)
	}

	const onSignClick = (data) => {
		if (isEmployer && isCreation) {
			const toSubmit = {
				terminationDate: data.terminationDate,
				terminationReasons: [data.terminationReason, ...additionalReasons],
				employeeRequest: data.employeeRequest,
				employeeConsent: data.employeeConsent,
				employerApprove: data.employerApprove,
				employeeAccountNumber: data.employeeAccountNumber,
				hrManagerPhone: data.hrManagerPhone,
				employeePhone: data.employeePhone,
			}
			handleSignWithoutEmployee(toSubmit, data.externalId)
		} else {
			handleSign({ termExternalId: termExternalId })
		}
	}

	const [disableContinue, setDisableContinue] = useState(false)

	const validateDate = (value) => {
		const valueFormat = getFormatDate(value)
		if (!valueFormat) setDisableContinue(false)

		if (!(valueFormat && getFormatDate(contract?.dateFrom))) return
		const dateFrom = new Date(getFormatDate(contract?.dateFrom))
		const valueDate = new Date(valueFormat)
		setDisableContinue(valueDate < dateFrom)
		setAnnuity(
			!!(
				isHarmfulWorkingConditions(dworkingConditionsCode) &&
				new Date(...value.split('.').reverse()).getFullYear() >= 2024 &&
				birthDate &&
				new Date().getFullYear() - new Date(...birthDate.split('.').reverse()).getFullYear() >= 55
			)
		)
	}

	const renderAnnuity = useMemo(() => {
		if (oopvSendByBin.loading) {
			return (
				<div className={styles.loadingWrapper}>
					<LoadingScreen>
						<span>{t('personal_affairs.loading_status')}</span>
					</LoadingScreen>
				</div>
			)
		} else {
			let label = {
				[LANGUAGE_ENUM.rus]: oopvSendByBin.data?.outRes.nameRu,
				[LANGUAGE_ENUM.kz]: oopvSendByBin.data?.outRes.nameKz,
			}
			return (
				<HarmfulCheckboxBlock
					isEdit={!isCreation}
					annuity={annuity}
					isFormVisible={terminateStatus[oopvSendByBin.data?.outRes?.code]?.isFormVisible || false}
					status={
						<div className={cl('mt-3', 'flex', 'align-center', 'gap-2')}>
							{terminateStatus[oopvSendByBin.data?.outRes?.code]?.icon &&
								terminateStatus[oopvSendByBin.data?.outRes?.code]?.icon}
							<strong>{t('personal_affairs.status')}</strong>
							<span>
								{t(terminateStatus[oopvSendByBin.data?.outRes?.code]?.preStatus)}. {label[lang]}
							</span>
						</div>
					}
				/>
			)
		}
	}, [oopvSendByBin])

	return (
		<Form>
			{isTeenager && isEmployer ? (
				<section>
					<div className={styles.item}>
						<Title>{t('representative_data')}</Title>
						<div className={styles.fields}>
							<Field name="representativeIin">
								{({ field, meta: { touched, error } }) => (
									<TextField
										{...field}
										type="number"
										hideNumberArrows
										label={t('representative_yin')}
										placeholder={t('enter_count_digitals', { count: 12 })}
										error={touched && error}
										maxLength={12}
										disabled={!isCreation}
									/>
								)}
							</Field>
							<div className={styles.fieldAndButton}>
								<div className={styles.buttonWrapper}>
									{flDataLoading && stateLoading['representativeData'] ? (
										<Loader />
									) : (
										<Button
											type="button"
											onClick={() => getFlDataRepresentative(values['representativeIin'])}
											disabled={!isCreation}
										>
											{t('find')}
										</Button>
									)}
								</div>
								<Field name="representativeName">
									{({ field, meta: { touched, error } }) => (
										<TextField
											{...field}
											type="text"
											isRequired
											disabled
											label={t('employee_initials')}
											placeholder={t('filled_automate')}
											error={touched && error}
										/>
									)}
								</Field>
							</div>
						</div>
					</div>
				</section>
			) : null}
			<div className={'mt-16'}>
				<Title>{createTerminationApp ? t('terminate_application') : t('e_term_contract')}</Title>
			</div>
			<Grid classes={{ root: styles.requisites__container }} container spacing={2}>
				<Grid item xs={5}>
					<label className={styles.label}>{t('terminate_date')}*</label>
					<Field name="terminationDate">
						{({ form }) => (
							<DatePicker
								minDate={new Date()}
								showIcon
								disabled={!isCreation}
								value={termDate?.split('-').reverse().join('') || null}
								onChange={(date) => {
									form.setFieldValue('terminationDate', date) // обновляем значение в Formik
									validateDate(date) // вызываем дополнительную валидацию даты
								}}
							/>
						)}
					</Field>
				</Grid>
				{employeeOrEmployer() ? (
					<Grid item xs={7}>
						<label className={styles.label}>{t('contract_termination_reason')}</label>
						<div>
							{additionalReasons.map((item, index) => {
								return (
									<div key={index}>
										<AdditionalReasonSelect
											index={index}
											setAdditionalReasons={setAdditionalReasons}
											value={item}
											terminationReasons={terminationReasonsForInputs}
											removeReason={handleRemoveReasonClick}
										/>
									</div>
								)
							})}
							{isCreation && (
								<div className="flex">
									<Field name="terminationReason">
										{({ field }) => (
											<SelectSearch
												disabled={!isCreation}
												{...field}
												className="flex-1"
												options={terminationReasonsForInputs}
											/>
										)}
									</Field>
									<Button
										disabled={!isCreation}
										transparent
										onClick={handleAddReasonClick}
										buttonClassName={styles.button}
									>
										<Add className={styles.iconGreen} />
										{t('add')}
									</Button>
								</div>
							)}

							{!isCreation &&
								termReasonsList &&
								termReasonsList.map((reason, index) => {
									return (
										<div className="flex" key={index}>
											<Field name="terminationReason">
												{({ field }) => (
													<SelectSearch
														disabled={!isCreation}
														{...field}
														value={reason?.nameRu}
														className="flex-1"
														options={terminationReasonsForInputs}
													/>
												)}
											</Field>
										</div>
									)
								})}
						</div>
					</Grid>
				) : (
					<Grid item xs={12}>
						<Field name="statement">
							{({ field, meta: { touched, error } }) => (
								<TextField
									type="text"
									fieldType={'textarea'}
									isRequired
									label={t('military.comment')}
									showCount
									disabled={!isCreation}
									maxLength={3500}
									rows={6}
									{...field}
								/>
							)}
						</Field>
					</Grid>
				)}
			</Grid>
			{annuity ? renderAnnuity : null}
			{!isCreation && note && (
				<>
					<div className={styles.etd__title}>{t('corruption.cause')} </div>
					<div className={styles.cause__text}>{note}</div>
				</>
			)}

			{isEmployer && (
				<div className="flex-1 mt-16">
					<Field name="terminationWithoutEmployeesConsent">
						{({ field }) => (
							<Checkbox
								{...field}
								label={t('contract_termination_without_employees_consent')}
								disabled={!isCreation}
								onChange={() => setFieldValue(field.name, !field.value)}
								value={field.value}
							/>
						)}
					</Field>
				</div>
			)}

			{isEmployee && isCreation && (
				<div className={styles.buttonBlock}>
					<Button transparent onClick={onClose}>
						{t('cancel')}
					</Button>
					<Button disabled={!isValid} type="submit">
						{t('sign_to_sign_employer')}
					</Button>
				</div>
			)}

			{isEmployer && isCreation && !values.terminationWithoutEmployeesConsent && (
				<div className={styles.buttonBlock}>
					<Button transparent onClick={onClose}>
						{t('cancel')}
					</Button>
					<Button disabled={!isValid || disableContinue} type="submit">
						{t('send_to_sign_worker')}
					</Button>
				</div>
			)}
			{isEmployer && isCreation && values.terminationWithoutEmployeesConsent && (
				<div className={styles.buttonBlock}>
					<Button disabled={!isValid || disableContinue} onClick={() => openSignWindow(values)}>
						{t('actions.sign')}
					</Button>
				</div>
			)}

			{!isCreation && isEmployer && (
				<div className={styles.buttonBlock}>
					{(isState.code === '4' || isState.code === '15') && (
						<span className={styles.button__wrapper}>
							<Button onClick={setOpenRevoke.bind(null, true)}>{t('actions.revoke')}</Button>
						</span>
					)}
					{!['2', '3', '4', '7'].includes(isState.code) && (
						<span className={styles.button__wrapper}>
							<Button onClick={handleOpenAdditional}>{t('notification_open_contract')}</Button>
						</span>
					)}
					{isState.code === '7' && (
						<>
							<span className={styles.button__wrapper}>
								<Button onClick={setOpenDecline.bind(null, true)}>{t('actions.reject')}</Button>
							</span>
							<span className={styles.button__wrapper}>
								<Button onClick={setOpenSign.bind(null, true)}>{t('actions.sign')}</Button>
							</span>
						</>
					)}
				</div>
			)}

			{!isCreation && isEmployee && (
				<div className={styles.buttonBlock}>
					{(isState.code === '7' || isState.code === '15') && (
						<span className={styles.button__wrapper}>
							<Button onClick={setOpenRevoke.bind(null, true)}>{t('actions.revoke')}</Button>
						</span>
					)}
					{!['2', '3', '4', '7', '15'].includes(isState.code) && (
						<span className={styles.button__wrapper}>
							<Button onClick={handleOpenAdditional}>{t('notification_open_contract')}</Button>
						</span>
					)}
					{isState.code === '4' && (
						<>
							<span className={styles.button__wrapper}>
								<Button onClick={setOpenDecline.bind(null, true)} disabled={isDisabledButton}>
									{t('actions.reject')}
								</Button>
							</span>
							<span className={styles.button__wrapper}>
								<Button onClick={setOpenSign.bind(null, true)} disabled={isDisabledButton}>
									{t('actions.sign')}
								</Button>
							</span>
						</>
					)}
				</div>
			)}
		</Form>
	)
}
